import {
  calculateRoofJoists,
  type RoofJoistResult,
  TRAGUIDEN_ROOF_JOIST_TABLE,
} from '../domain/snowzone/calculateRoofJoists';
import { ENGINE_JOIST_H_M, ENGINE_STUD_D_M } from './constants';
import {
  TWO_ROOM_DEFAULT_ENTRY_WALL,
} from './twoRoomConstants';
import type { BuildInput } from './types';

type RoofJoistSizingInput = Pick<BuildInput, 'dimensions' | 'postalCode' | 'climateProfile' | 'overrides' | 'roofConfig' | 'entryWall' | 'glazingStyle' | 'siting'>;

const DEFAULT_FLOOR_JOIST_DEPTH_MM = Math.round(ENGINE_JOIST_H_M * 1000);

export function getFloorJoistDepthMm(): number {
  return DEFAULT_FLOOR_JOIST_DEPTH_MM;
}

export function getFloorJoistDimensionLabel(): string {
  return `45×${DEFAULT_FLOOR_JOIST_DEPTH_MM} mm`;
}

export function getFloorJoistPriceRuleId(): string {
  return 'prices.golvBjalkar';
}

/**
 * Clear structural span of a roof joist across a rectangular sauna.
 *
 * `BuildInput.dimensions.width`/`length` are the framing OUTER dimensions
 * (the studs live inside ±W/2 — the cladding is added outside that), while
 * `calculateRoofJoists` wants the span BETWEEN the load-bearing walls. That is
 * the same definition the public snow-zone calculator shows users: "avstånd
 * mellan bärande väggar". Feeding the raw outer dimension made a 3.6 m wide
 * sauna — real clear span 3.41 m, inside the heuristic range — trip the
 * 3.5 m `limträbalk krävs / anlita konstruktör` hard error (issue #49 item 1).
 *
 * Only the two stud depths come off. The interior lining stack (bastufolie +
 * liggande glespanel + stående bastupanel, `ENGINE_INTERIOR_LINING_D_M +
 * ENGINE_PANEL_T_M`, 38.5 mm per wall) hangs BELOW the joists on the room side
 * and carries no roof load, so deducting it too would understate the span by
 * 77 mm in the unconservative direction. The joists bear on the hammarband,
 * which is the full stud depth.
 *
 * Shared by `resolveAdvisoryRoofJoists` (square + two-room) and the
 * `withRoofJoists` fallback in the enrichment pipeline so the two can never
 * disagree about what "span" means.
 */
export function getRoofJoistClearSpanM(outerDimensionM: number): number {
  return Math.max(0, outerDimensionM - 2 * ENGINE_STUD_D_M);
}

export function resolveAdvisoryRoofJoists(input: RoofJoistSizingInput): RoofJoistResult | undefined {
  // An indoor sauna's ceiling joists carry only the ceiling (issue 156): the
  // existing building takes the snow, so a snow-zone sizing would be advice
  // for a load that never reaches them. They build at the default section.
  if (input.roofConfig?.kind === 'barrel' || input.siting === 'indoor') {
    return undefined;
  }

  // Both rendered roof systems span across X; changing entry/glazing does not rotate them.
  const spanMeters = getRoofJoistClearSpanM(input.dimensions.width);
  const requiresLateralBracing = input.glazingStyle === 'panorama';
  const roofJoists = calculateRoofJoists(
    {
      spanMeters,
      postalCode: input.postalCode,
      snowZone: input.climateProfile?.snowZone,
    },
    input.overrides,
  );

  if ((input.entryWall ?? TWO_ROOM_DEFAULT_ENTRY_WALL) === TWO_ROOM_DEFAULT_ENTRY_WALL && !requiresLateralBracing) {
    return roofJoists;
  }

  return {
    ...roofJoists,
    spanDirection: 'width',
    spanMeters,
    bearingWalls: ['left', 'right'],
    requiresLateralBracing,
    lateralBracingWarning: requiresLateralBracing
      ? 'Panoramavägg är också takbärande vägg. Kontrollera lateral avstyvning och infästning av header.'
      : null,
  };
}

/**
 * Deepest section in the span table. When no conventional joist carries the
 * span (the result says limträ or konstruktör, `depthMm: 0`) the 3D and the
 * roof build-up still need a depth; they draw this one, and every BOM and
 * cut label says it is a placeholder for a glulam beam (issue 170).
 */
export const GLULAM_PLACEHOLDER_DEPTH_MM =
  TRAGUIDEN_ROOF_JOIST_TABLE[TRAGUIDEN_ROOF_JOIST_TABLE.length - 1].depthMm;

export function getStructuralRoofJoists(roofJoists?: RoofJoistResult): RoofJoistResult | undefined {
  if (!roofJoists) {
    return undefined;
  }

  if (roofJoists.depthMm > 0) {
    return roofJoists;
  }

  return {
    ...roofJoists,
    depthMm: GLULAM_PLACEHOLDER_DEPTH_MM,
    dimension: `45×${GLULAM_PLACEHOLDER_DEPTH_MM} mm`,
    glulamPlaceholder: true,
  };
}

/** True when the drawn joist stands in for a glulam beam nobody has sized. */
export function isGlulamPlaceholder(roofJoists?: RoofJoistResult): boolean {
  return getStructuralRoofJoists(roofJoists)?.glulamPlaceholder === true;
}

export function getRoofJoistDepthMm(roofJoists?: RoofJoistResult): number {
  return getStructuralRoofJoists(roofJoists)?.depthMm ?? DEFAULT_FLOOR_JOIST_DEPTH_MM;
}

export function getRoofJoistDepthM(roofJoists?: RoofJoistResult): number {
  return getRoofJoistDepthMm(roofJoists) / 1000;
}

/** BOM material name: conventional timber, or the glulam that replaces it. */
export function getRoofJoistMaterialName(roofJoists?: RoofJoistResult): string {
  return isGlulamPlaceholder(roofJoists) ? 'Takbjälkar (limträ)' : 'Takbjälkar (K-virke)';
}

export function getRoofJoistDimensionLabel(roofJoists?: RoofJoistResult): string {
  if (isGlulamPlaceholder(roofJoists)) {
    return GLULAM_PLACEHOLDER_DIMENSION_LABEL;
  }
  return `45×${getRoofJoistDepthMm(roofJoists)} mm`;
}

/** Shown on the BOM row; `translateDimension` maps it for intl. */
export const GLULAM_PLACEHOLDER_DIMENSION_LABEL =
  `Dimensioneras av konstruktör (ritad som 45×${GLULAM_PLACEHOLDER_DEPTH_MM}, riktpris limträ 56×225)`;

export function getRoofJoistCutDimensionLabel(roofJoists?: RoofJoistResult): string {
  if (isGlulamPlaceholder(roofJoists)) {
    return 'limträ, dimensioneras av konstruktör';
  }
  return getRoofJoistDimensionLabel(roofJoists).replace(' mm', '');
}

/**
 * Purchase fields for the roof-joist cut row. Without a dimension the part
 * name "Takbjälkar" maps to 45×145 in the purchase plan. The glulam
 * placeholder buys nothing: the BOM row carries a budget price (56×225), but
 * the section is the designer's, so the plan must not list a board to buy.
 */
export function getRoofJoistCutPurchase(
  roofJoists?: RoofJoistResult,
): { purchaseDimension: string } | { engineerSized: true } {
  return isGlulamPlaceholder(roofJoists)
    ? { engineerSized: true }
    : { purchaseDimension: `45x${getRoofJoistDepthMm(roofJoists)}` };
}

export function getRoofJoistPriceRuleId(roofJoists?: RoofJoistResult): string {
  if (isGlulamPlaceholder(roofJoists)) {
    return 'prices.limtraTakbalk';
  }
  return `prices.takbjalkar45x${getRoofJoistDepthMm(roofJoists)}`;
}
